import axios from 'axios'
import * as common from './common'
import * as api from './api'
import { pyunMerchant } from '../../config/pyunConfig'

/**
 * 获取微信公众号AppID（用于支付云服务）
 * @param {string|number} sid - 车场ID
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<string|null>} 返回微信公众号AppID，如果获取失败则返回null
 */
export const getGzhAppIdWx = async (sid, options = {}) => {
  const {
    contentType = 'application/json'
  } = options

  try {
    const res = await axios({
      url: common.cloudPayUrl + '/pub/pay.bill.park_plate.get_gzh_appid',
      method: 'POST',
      data: { sid },
      header: {
        'content-type': contentType
      }
    })

    const d = res && res.data
    if (d.code === 10000) {
      return d.appid
    } else {
      console.error('获取微信公众号AppID失败:', d.msg || '未知错误')
      return null
    }
  } catch (error) {
    console.error('获取微信公众号AppID异常:', error)
    return null
  }
}

/**
 * 获取支付跳转URL
 * @param {string} payWay - 支付方式
 * @param {string|number} sid - 车场ID
 * @param {string} plateNumber - 车牌号
 * @param {Object} tempQueryParams - 临时查询参数
 * @param {string} redirectPath - 重定向路径，默认为 'payMoney'
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<Object|null>} 返回包含open_url和pay_type的对象，如果获取失败则返回null
 */
export const getOpenUrl = async (payWay, sid, plateNumber, tempQueryParams, redirectPath = 'payMoney', options = {}) => {
  const {
    contentType = 'application/json'
  } = options

  try {
    const local = common.cloudPayUrl + '/#' + (redirectPath.startsWith('/') ? redirectPath : '/' + redirectPath)
    const res = await axios({
      url: common.cloudPayUrl + '/pub/' + api.urlList.get_openid_url,
      method: 'POST',
      data: {
        pay_way: payWay,
        sid: sid,
        plate_number: plateNumber,
        tempQueryParams: tempQueryParams,
        redirect_url: local
      },
      header: {
        'content-type': contentType
      }
    })

    const d = res && res.data
    if (d.code === 10000) {
      return {
        open_url: d.open_url,
        pay_type: d.pay_type
      }
    } else {
      console.error('获取支付跳转URL失败:', d.msg || '未知错误')
      return null
    }
  } catch (error) {
    console.error('获取支付跳转URL异常:', error)
    return null
  }
}

/**
 * 获取微信AppID（用于停车云服务）
 * @param {string|number} sid - 车场ID
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<string|null>} 返回微信AppID，如果获取失败则返回null
 */
export const getAppIdWx = async (sid, options = {}) => {
  const {
    contentType = 'application/json'
  } = options

  try {
    const res = await axios({
      url: common.cloudParkingUrl + '/s2p/' + api.urlList.wx_get_appid,
      method: 'POST',
      data: { sid },
      header: {
        'content-type': contentType
      }
    })

    const d = res && res.data
    if (d.code === 10000) {
      return d.app_id
    } else {
      console.error('获取微信AppID失败:', d.msg || '未知错误')
      return null
    }
  } catch (error) {
    console.error('获取微信AppID异常:', error)
    return null
  }
}

/**
 * 获取支付宝AppID
 * @param {string|number} sid - 车场ID
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<Object|null>} 返回支付宝AppID对象，包含auth_app_id和app_id，如果获取失败则返回null
 */
export const getAppIdZfb = async (sid, options = {}) => {
  const {
    contentType = 'application/json'
  } = options

  try {
    const res = await axios({
      url: common.cloudParkingUrl + '/s2p/' + api.urlList.zfb_get_appid,
      method: 'POST',
      data: { sid },
      header: {
        'content-type': contentType
      }
    })

    const d = res && res.data
    if (d.code === 10000) {
      return {
        auth_app_id: d.auth_app_id,
        app_id: d.app_id
      }
    } else {
      console.error('获取支付宝AppID失败:', d.msg || '未知错误')
      return null
    }
  } catch (error) {
    console.error('获取支付宝AppID异常:', error)
    return null
  }
}

/**
 * 构建 P 云券包 event.redirect 参数（divert_provider 使用 pyunConfig 固定商户号）
 * @param {string|number} sid - 车场ID（保留参数，兼容调用方）
 * @param {string} deputeId - 支付委托单 pay_id
 * @returns {Promise<Object>} redirect 参数对象
 */
export const getPyunCouponRedirectParams = async (sid, deputeId) => {
  const params = { depute_id: deputeId }
  if (pyunMerchant) {
    params.divert_provider = pyunMerchant
  }
  return params
}

/**
 * 获取车场的支付平台
 * @param {string|number} sid - 车场ID
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<number>} 返回支付平台类型：1-微信，2-支付宝
 */
export const getPayTypeBySid = async (sid, options = {}) => {
  const {
    contentType = 'application/json'
  } = options

  try {
    const res = await axios({
      url: common.cloudPayUrl + '/pub/' + api.urlList.pay.get_sid_pay_way,
      method: 'POST',
      data: { sid },
      headers: {
        'content-type': contentType
      }
    })

    const d = res && res.data
    console.log('获取到车场的支付平台init', d)

    if (d.code === 10000) {
      console.log('获取到车场的支付平台', d)
      console.log('获取设备平台', common.getUserAgent())

      if (common.getUserAgent() === 'wx') {
        return Number(d.sid_pay_way.wechat) === 0 ? 1 : d.sid_pay_way.wechat
      } else if (common.getUserAgent() === 'zfb') {
        return Number(d.sid_pay_way.alipay) === 0 ? 2 : d.sid_pay_way.alipay
      }
    } else {
      return Promise.reject(new Error(d.msg || 'Unknown error'))
    }
  } catch (error) {
    console.error('Error fetching payment type:', error.message || error)
    throw error
  }
}
